<script lang="ts">
  import { tick } from 'svelte';
  import Screen from '../lib/design/Screen.svelte';
  import EggMark from '../lib/design/EggMark.svelte';
  import AnswerBody from '../lib/design/AnswerBody.svelte';
  import PeekSheet from '../lib/design/PeekSheet.svelte';
  import { link, href } from '../lib/router.svelte';
  import { nest } from '../lib/nest.svelte';
  import { peck } from '../lib/peck.svelte';
  import { humanize } from '../shared/slug.js';

  const examples = [
    'What do I know about our onboarding process?',
    "Summarise this week's activity",
    'What did we decide about timelines?',
  ];

  let input = $state('');
  let peeked = $state<string | null>(null);
  let scroller = $state<HTMLElement | null>(null);

  const empty = $derived(peck.turns.length === 0);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    const q = input.trim();
    if (!q || peck.busy) return;
    input = '';
    const p = peck.ask(q);
    await tick();
    scroller?.scrollIntoView({ block: 'end' });
    await p;
    await tick();
    scroller?.scrollIntoView({ block: 'end' });
  }
</script>

<Screen title="Ask" sync={nest.status}>
  {#if empty}
    <div class="intro">
      <EggMark size={44} />
      <p class="slogan">Don't browse your notes. Peck them. Get answers.</p>
      <p class="hint">
        Answered only from your nest, with <code>[[links]]</code> to the pages used.
      </p>
      {#if nest.count === 0}
        <p class="hint">
          Your nest isn't synced yet — <a use:link href={href('/read')}>open Read</a> to pull it in.
        </p>
      {:else}
        <div class="pills">
          {#each examples as ex (ex)}
            <button class="pill" onclick={() => (input = ex)}>{ex}</button>
          {/each}
        </div>
      {/if}
    </div>
  {:else}
    <div class="transcript" bind:this={scroller}>
      {#each peck.turns as turn (turn.id)}
        <div class="turn">
          <p class="q">{turn.question}</p>

          {#if turn.status === 'retrieving'}
            <p class="working">Searching your nest…</p>
          {:else}
            {#if turn.cited.length}
              <div class="cites">
                {#each turn.cited as c (c.slug)}
                  <button class="cite" onclick={() => (peeked = c.slug)}>{humanize(c.slug)}</button>
                {/each}
              </div>
            {/if}

            {#if turn.status === 'answering'}
              <p class="working">
                Reading {turn.cited.length} page{turn.cited.length === 1 ? '' : 's'}…
              </p>
            {:else if turn.status === 'error'}
              <p class="err">{turn.error}</p>
              <button class="retry" onclick={() => peck.retry(turn.id)} disabled={peck.busy}>
                Retry
              </button>
            {:else}
              <AnswerBody text={turn.answer} onpeek={(s) => (peeked = s)} />
              {#if turn.callId}
                <div class="actions">
                  <button
                    class="act"
                    class:on={turn.rated === 1}
                    aria-label="Helpful"
                    onclick={() => peck.rate(turn.id, 1)}>👍</button
                  >
                  <button
                    class="act"
                    class:on={turn.rated === 0}
                    aria-label="Not helpful"
                    onclick={() => peck.rate(turn.id, 0)}>👎</button
                  >
                  <button
                    class="act text"
                    onclick={() => peck.regenerate(turn.id)}
                    disabled={peck.busy}>Regenerate</button
                  >
                  {#if turn.model}<span class="model">{turn.model}</span>{/if}
                </div>
              {/if}
            {/if}
          {/if}
        </div>
      {/each}
    </div>
    <div class="clear-row">
      <button class="linkish" onclick={() => peck.reset()} disabled={peck.busy}>New question</button
      >
    </div>
  {/if}

  <form class="composer" onsubmit={submit}>
    <textarea
      bind:value={input}
      rows="1"
      placeholder="Ask your nest a question…"
      onkeydown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          e.currentTarget.form?.requestSubmit();
        }
      }}
    ></textarea>
    <button class="peck" type="submit" disabled={!input.trim() || peck.busy}>
      {peck.busy ? '…' : 'Peck'}
    </button>
  </form>
</Screen>

{#if peeked}
  <PeekSheet slug={peeked} onclose={() => (peeked = null)} />
{/if}

<style>
  .intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 14px;
    padding: 40px 0 24px;
  }
  .slogan {
    font-family: var(--serif);
    font-size: 21px;
    line-height: 1.35;
    max-width: 24ch;
  }
  .hint {
    color: var(--ink-soft);
    font-size: 14px;
  }
  .hint code {
    font-family: var(--mono);
    font-size: 0.85em;
    background: var(--paper);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .hint a {
    color: var(--rooster);
    text-decoration: underline;
  }
  .pills {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    width: 100%;
  }
  .pill {
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 10px 16px;
    background: var(--cream-2);
    font-size: 13px;
    color: var(--ink-soft);
    text-align: left;
  }
  .transcript {
    display: flex;
    flex-direction: column;
    gap: 28px;
    padding-bottom: 96px;
  }
  .turn {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .q {
    align-self: flex-end;
    max-width: 85%;
    background: var(--ink);
    color: var(--cream);
    padding: 9px 14px;
    border-radius: 14px 14px 4px 14px;
    font-size: 14px;
  }
  .working {
    color: var(--ink-faint);
    font-family: var(--mono);
    font-size: 12px;
    letter-spacing: 0.04em;
  }
  .cites {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .cite {
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 4px 10px;
    background: var(--cream-2);
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    color: var(--ink-soft);
  }
  .err {
    color: var(--rooster-deep);
    font-size: 14px;
  }
  .retry {
    align-self: flex-start;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 7px 14px;
    font-family: var(--mono);
    font-size: 12px;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }
  .act {
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 13px;
    line-height: 1.4;
    background: var(--cream-2);
  }
  .act.on {
    border-color: var(--rooster);
    background: var(--paper);
  }
  .act.text {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-soft);
  }
  .act:disabled {
    opacity: 0.4;
  }
  .model {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--ink-faint);
  }
  .clear-row {
    position: sticky;
    bottom: 88px;
    display: flex;
    justify-content: center;
    pointer-events: none;
  }
  .linkish {
    pointer-events: auto;
    color: var(--rooster);
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: var(--cream);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 6px 14px;
  }
  .linkish:disabled {
    opacity: 0.4;
  }
  .composer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(var(--tabbar-h) + env(safe-area-inset-bottom));
    display: flex;
    gap: 8px;
    padding: 10px 14px;
    background: var(--cream);
    border-top: 1px solid var(--line);
  }
  @media (min-width: 900px) {
    .composer {
      left: 84px;
    }
  }
  textarea {
    flex: 1;
    resize: none;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 10px 12px;
    background: var(--cream-2);
    max-height: 40vh;
  }
  .peck {
    align-self: flex-end;
    border-radius: 12px;
    padding: 10px 18px;
    background: var(--rooster);
    color: var(--cream);
    font-family: var(--mono);
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.03em;
  }
  .peck:disabled {
    opacity: 0.4;
  }
</style>
